import React, { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import DialogActions from '@mui/material/DialogActions';
import { useDropzone } from 'react-dropzone';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import BtnGradient from 'src/components/common/button/BtnGradient';
import { getToken } from 'src/utils/auth';
import { postFormDataAPI } from 'src/utils/api';
import { getToday } from 'src/common/helper';

interface UploadImageProps {
  open: boolean;
  onClose: () => void;
  patient: any;
}

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#F5F8FE',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  cursor: 'pointer',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const UploadImage: React.FC<UploadImageProps> = (props) => {
  const [files, setFiles] = useState<any>([]);
  const handleClose = () => {
    props.onClose();
  };

  const onDrop = useCallback((acceptedFiles: any) => {
    acceptedFiles.map((file: any) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setFiles((prevState: any) => [...prevState, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: {
        'image/png': ['.png', '.jpg', '.jpeg'],
      },
    });

  const style: any = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleClearAll = () => {
    setFiles([]);
  };

  const handleSave = () => {
    console.log(files);
    const token = getToken();
    
    files.forEach((file: any, index: number) => {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('type', 'image');
      formData.append('patientid', props.patient?.id || '');

      console.log(file);
      console.log(file.name);
      formData.append(`files[${index}]`, file);
      formData.append(`customfilename[${index}]`, file.name);
      formData.append(`createdat[${index}]`, getToday());

      try {
        postFormDataAPI(`/file/upload?token=${token}`, formData).then((res) => {
          if (res.code === 200) {
            console.log('upload success');
          }
        });
      } catch (error) {
        console.log(error);
      }
    });
  };

  const handleRemoveFile = (index: number) => {
    let newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      //   slotProps={{ backdrop: { invisible: true } }}
      sx={{
        '& .MuiDialog-paper': {
          m: '0px !important',
          width: '600px',
          maxHeight: '90vh',
          bgcolor: '#FAFDFF',
        },
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px 24px 0' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography fontSize={'20px'} fontWeight={500}>
            画像ファイルアップロード
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 24px 24px',
        }}
      >
        <section className='container'>
          <div {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <Stack spacing={'8px'} alignItems={'center'}>
              <Box
                component={'img'}
                src='/images/icons/ic-upload-file.svg'
                alt='upload-file'
                sx={{ cursor: 'pointer' }}
                width={'24px'}
              />
              <Typography fontSize={'14px'} fontWeight={500}>
                ここにファイルをドラッグ&ドロップ
              </Typography>
              <Typography fontSize={'10px'}>または</Typography>
              <Typography fontSize={'14px'} color={'#23B7E5'}>
                ファイルを選択する
              </Typography>
              <Typography fontSize={'10px'}>
                アップロードできるファイル形式はIMAGEのみです。
              </Typography>
            </Stack>
          </div>
          {/* <aside>
            <ul>{files}</ul>
          </aside> */}
        </section>
        {files.length > 0 && (
          <Grid container spacing={2} mt={'20px'}>
            {files.map((file: any, index: number) => {
              return (
                <Grid item xs={3} key={index}>
                  <Stack spacing={'10px'}>
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      spacing={'8px'}
                    >
                      <Typography
                        fontSize={'10px'}
                        sx={{
                          wordBreak: 'break-word',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          lineClamp: 2,
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                        height={'30px'}
                      >
                        {file.name}
                      </Typography>
                      <Box
                        component={'img'}
                        src='/images/icons/ic-trash.svg'
                        alt='upload-file'
                        sx={{ cursor: 'pointer' }}
                        width={'24px'}
                        onClick={() => handleRemoveFile(index)}
                      />
                    </Stack>
                    <Box
                      component={'img'}
                      src={file.preview}
                      alt='upload'
                      sx={{
                        cursor: 'pointer',
                        border: '1px solid #F3F3F3',
                      }}
                      width={'100%'}
                      height={'auto'}
                    />
                    {/* <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      spacing={'8px'}
                    >
                      <Typography fontSize={'12px'}>
                        {props.patient?.patientno || ''}
                      </Typography>
                      <Typography fontSize={'12px'}>
                        {props.patient?.fullname || ''}
                      </Typography>
                    </Stack> */}
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '24px' }}>
        <BtnOutlineGradient handleButtonClick={handleClose} text='キャンセル' />
        {files.length > 0 && (
          <BtnOutlineGradient
            handleButtonClick={handleClearAll}
            text='すべてクリア'
          />
        )}
        <BtnGradient handleButtonClick={handleSave} text='取込' />
      </DialogActions>
    </Dialog>
  );
};

export default UploadImage;
